import { ArrowDownToLine, Clock, Loader2, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import BannedBanner from '@/components/member/BannedBanner';
import LiveWithdrawPanel from '@/components/member/widgets/LiveWithdrawPanel';

/**
 * Withdrawal subpage.
 *
 * When LIVE_PAYMENTS_ENABLED=true (read from /api/public/config), renders
 * the full live request flow via <LiveWithdrawPanel>. Otherwise renders the
 * "Activation Pending" placeholder so users know payouts aren't online yet.
 *
 * The backend wallet.withdraw() plumbing, the active-SubPack rule, and the
 * admin Mass Payout pipeline are wired and tested regardless of the flag.
 */
const WithdrawSubpage = ({ me, onRefresh }) => {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    const backendUrl = (typeof process !== 'undefined' && process.env.REACT_APP_BACKEND_URL) || '';
    let alive = true;
    fetch(`${backendUrl}/api/public/config`, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (alive) setEnabled(Boolean(d?.live_payments_enabled));
      })
      .catch(() => alive && setEnabled(false));
    return () => {
      alive = false;
    };
  }, []);

  if (enabled === null) {
    return (
      <div className="sc-glass rounded-2xl p-10 flex items-center justify-center gap-2 text-[var(--sc-text-dim)]" data-testid="subpage-withdraw-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading…
      </div>
    );
  }

  // Live mode \u2014 render the real withdrawal flow.
  if (enabled) {
    return (
      <div className="space-y-6" data-testid="subpage-withdraw">
        {/* Patch v18.0 — banned-account banner. When the member is flagged,
            we render it above LiveWithdrawPanel; the backend also refuses
            the withdrawal with HTTP 400 ACCOUNT_FLAGGED regardless of the
            frontend state, so this is display-only defense-in-depth. */}
        <BannedBanner me={me} />
        <LiveWithdrawPanel me={me} onRefresh={onRefresh} />
      </div>
    );
  }

  // Disabled (master flag off) \u2014 fall through to the placeholder.
  return (
    <div className="space-y-6" data-testid="subpage-withdraw">
      <BannedBanner me={me} />
      <section className="sc-glass-strong rounded-2xl p-7 md:p-10 relative overflow-hidden" data-testid="withdraw-pending-panel">
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
        />
        <div className="relative z-10 max-w-2xl">
          <div className="sc-pill">
            <span className="sc-dot" />
            Crypto Withdrawal
          </div>
          <h2 className="font-display font-bold text-2xl md:text-3xl mt-4">Withdrawals will be available shortly</h2>
          <p className="text-[15px] text-[var(--sc-text-dim)] mt-3 leading-relaxed">
            Your platform admin is finalizing the secure crypto payout provider connection. Once activated, you'll be able to request payouts to your
            <strong className="text-[#FFC20E]"> LTC</strong>, <strong className="text-[#FFC20E]">TRX</strong>, or{' '}
            <strong className="text-[#FFC20E]">DOGE</strong> wallet directly from this page.
          </p>

          <div className="mt-6 flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
            <Clock className="w-3.5 h-3.5 text-[#FFC20E]" />
            Activation pending
          </div>

          <div className="mt-6 rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(17,17,17,0.45)] p-4 flex items-start gap-3">
            <ArrowDownToLine className="w-4 h-4 mt-0.5 text-[#FFC20E] shrink-0" />
            <div className="text-[13px] text-[var(--sc-text-dim)] leading-relaxed">
              When live, payouts will require at least one active Sub Pack and a minimum withdrawal of $10.00 USD. Save your wallet addresses in the Profile
              page so they're ready to use the moment payouts activate.
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(17,17,17,0.45)] p-4 flex items-start gap-3">
            <Send className="w-4 h-4 mt-0.5 text-[#FFC20E] shrink-0" />
            <div className="text-[13px] text-[var(--sc-text-dim)] leading-relaxed">
              Approved payouts are batched and sent by the platform admin via the payment provider's mass payout system — typically within a few hours of
              approval.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WithdrawSubpage;
